import type { ReactionWeightsFormState } from '../types/reactionWeights';
import type { ExperimentPersonaBinding } from '../types/vuser';
import { getDuplicatePersonaBindingMessage } from './experimentPersonaPairs';
import {
  prefixFieldErrors,
  type ModalFieldErrors,
} from './modalFormValidation';
import { validateReactionWeightsForm } from '../types/reactionWeights';

export const EXPORT_DATASET_NAME_ID = 'datasets-export-dataset-name';
export const EXPORT_PERIOD_SECTION_ID = 'datasets-export-period-section';
export const EXPORT_USERS_SECTION_ID = 'datasets-export-source-users';
export const EXPORT_DIALOGS_SECTION_ID = 'datasets-export-source-dialogs';
export const EXPORT_FILTERS_ID = 'datasets-export-filters';
export const EXPORT_ERROR_BANNER_ID = 'datasets-export-error-banner';
export const EXPORT_REACTION_WEIGHTS_PREFIX = 'datasets-export-reaction-weights';

export const START_EXP_DATASET_NAME_ID = 'start-exp-dataset-name';
export const START_EXP_VIRTUAL_USERS_ID = 'start-exp-virtual-users';
export const START_EXP_USER_PERSONA_PAIRS_ID = 'start-exp-user-persona-pairs';
export const START_EXP_ADMIN_PERSONA_PAIRS_ID = 'start-exp-admin-persona-pairs';
export const START_EXP_FIRST_MESSAGE_ID = 'start-exp-first-message';
export const START_EXP_DIALOG_COUNT_ID = 'start-exp-dialog-count';
export const START_EXP_MESSAGES_PER_DIALOG_ID = 'start-exp-messages-per-dialog';
export const START_EXP_ERROR_BANNER_ID = 'start-exp-error-banner';
export const START_EXP_REACTION_WEIGHTS_PREFIX = 'datasets-start-exp-reaction-weights';

export function validateExportForm(params: {
  dataset_name: string;
  requireDatasetName: boolean;
  hasPeriod: boolean;
  hasAtLeastOneSignal: boolean;
  selectedUserCount: number;
  selectedDialogCount: number;
  reactionWeights: ReactionWeightsFormState;
}): ModalFieldErrors {
  const errors: ModalFieldErrors = {};

  if (params.requireDatasetName && !params.dataset_name.trim()) {
    errors[EXPORT_DATASET_NAME_ID] = 'Dataset name is required';
  }
  if (!params.hasPeriod) {
    errors[EXPORT_PERIOD_SECTION_ID] = 'Training period is required';
  }
  if (params.selectedUserCount === 0) {
    errors[EXPORT_USERS_SECTION_ID] = 'Select at least one user.';
  }
  if (params.selectedDialogCount === 0) {
    errors[EXPORT_DIALOGS_SECTION_ID] = 'Select at least one dialog.';
  }
  if (!params.hasAtLeastOneSignal) {
    errors[EXPORT_FILTERS_ID] = 'Select at least one signal filter before continuing.';
  }

  Object.assign(
    errors,
    prefixFieldErrors(EXPORT_REACTION_WEIGHTS_PREFIX, validateReactionWeightsForm(params.reactionWeights))
  );

  return errors;
}

export function validateStartExperimentForm(params: {
  experiment_name: string;
  useCustomPersonaPairs: boolean;
  userPersonaPairs: ExperimentPersonaBinding[];
  adminPersonaPairs: ExperimentPersonaBinding[];
  virtualUserIds: string[];
  virtualUsersMissingPersonaNames: string[];
  first_message: string;
  dialog_count: number;
  messages_per_dialog: number;
  maxDialogs: number;
  maxMessagesPerDialog: number;
  reactionWeights: ReactionWeightsFormState;
}): ModalFieldErrors {
  const errors: ModalFieldErrors = {};

  if (!params.experiment_name.trim()) {
    errors[START_EXP_DATASET_NAME_ID] = 'Dataset name is required';
  }

  if (params.useCustomPersonaPairs) {
    const userPairs = params.userPersonaPairs.filter((p) => p.virtual_user_id && p.persona_id);
    if (userPairs.length === 0) {
      errors[START_EXP_USER_PERSONA_PAIRS_ID] = 'At least one virtual user + persona pair is required';
    } else {
      const duplicateUserPairsMessage = getDuplicatePersonaBindingMessage(userPairs);
      if (duplicateUserPairsMessage) {
        errors[START_EXP_USER_PERSONA_PAIRS_ID] = duplicateUserPairsMessage;
      }
    }

    const adminPairs = params.adminPersonaPairs.filter((p) => p.virtual_user_id && p.persona_id);
    const duplicateAdminPairsMessage = getDuplicatePersonaBindingMessage(adminPairs);
    if (duplicateAdminPairsMessage) {
      errors[START_EXP_ADMIN_PERSONA_PAIRS_ID] = duplicateAdminPairsMessage;
    }
  } else if (params.virtualUserIds.length === 0) {
    errors[START_EXP_VIRTUAL_USERS_ID] = 'At least one virtual user is required';
  } else if (params.virtualUsersMissingPersonaNames.length > 0) {
    errors[START_EXP_VIRTUAL_USERS_ID] =
      `Virtual user must have a persona: ${params.virtualUsersMissingPersonaNames.join(', ')}. Assign a persona in Virtual User Service.`;
  }

  if (!params.first_message.trim()) {
    errors[START_EXP_FIRST_MESSAGE_ID] = 'First message is required';
  }
  if (!Number.isFinite(params.dialog_count) || params.dialog_count <= 0) {
    errors[START_EXP_DIALOG_COUNT_ID] = 'Dialog count must be greater than 0';
  } else if (params.dialog_count > params.maxDialogs) {
    errors[START_EXP_DIALOG_COUNT_ID] = `Dialog count cannot exceed ${params.maxDialogs}`;
  }
  if (!Number.isFinite(params.messages_per_dialog) || params.messages_per_dialog <= 0) {
    errors[START_EXP_MESSAGES_PER_DIALOG_ID] = 'Messages per dialog must be greater than 0';
  } else if (params.messages_per_dialog > params.maxMessagesPerDialog) {
    errors[START_EXP_MESSAGES_PER_DIALOG_ID] =
      `Messages per dialog cannot exceed ${params.maxMessagesPerDialog}`;
  }

  Object.assign(
    errors,
    prefixFieldErrors(START_EXP_REACTION_WEIGHTS_PREFIX, validateReactionWeightsForm(params.reactionWeights))
  );

  return errors;
}


// Ниже этой доли сплит бессмысленен: бекенд требует минимум примеров в трейне
// и валидации (`minDatasetExportTrainExamples`/`...ValidationExamples`), а нулевой
// сплит принимает молча и роняет уже тюнинг.
export const MIN_SPLIT_PCT = 5;

/** Пересчёт трёх долей после движения одного ползунка.
 *
 *  Тест берёт долю у валидации, а исчерпав её — у трейна. Отдельной функцией,
 *  потому что дефект был именно здесь: прежняя версия брала только у валидации
 *  и при 25% оставляла её ровно нулём — превью отдавало 400, а импорт молча
 *  создавал датасет без валидации, и это всплывало уже на тюнинге. */
export function nextSplitRatios(
  current: { train: number; validation: number; test: number },
  change: { train?: number; test?: number },
): { train: number; validation: number; test: number } {
  const test = Math.max(0, Math.min(change.test ?? current.test, 100 - MIN_SPLIT_PCT * 2));
  const train = Math.max(
    MIN_SPLIT_PCT,
    Math.min(change.train ?? current.train, 100 - MIN_SPLIT_PCT - test),
  );
  return { train, validation: 100 - train - test, test };
}
